import { memo } from 'react'
import cx from 'classnames'
import { Field, FieldArray } from 'redux-form'
import _ from 'lodash'
import { Button, SpecAdditionalTips, Tip } from '@vzmi/shp_shared_compo'
import { ATTRIBUTE_CONSTRAINTS_TYPE } from '../../../../../constants/proposalTypes'
import { required as fieldRequried } from '../../../../../lib/FormHelper'
import {
  skipRequiredThenValidate
} from '../../../../Proposal/Common/ProposalForm/Shared/validation/validations'
import { filterHtmlTag, filterHtmlTagAndSpace, bsmiNormalizer } from '../../../../Proposal/Common/ProposalForm/Shared/normalizers'
import {
  FormRow,
  InputField,
  InputCountField,
  MenuSelectField,
  CheckboxMenuSelectField,
  TextareaField,
  CheckboxInputField
} from '../../../../Common/Form'
import { getFieldName } from '../../../helper/batch'
import styles from './index.css'
import { DEFAULT_CUSTOM_ATTRIBUTE_FIELD } from '../../helper'
import { getIsBSMIName } from '../../../../Proposal/Common/ProposalForm/Shared/util'
import BSMIIconPopoverLabel from '../../../../Proposal/Common/BSMIIconPopoverLabel'

export const checkFieldNameNotDuplicated = field => (value, allValues) => {
  // 從目前要驗證的欄位路徑取得該賣場底下全部的規格項目
  const attributes = _.get(allValues, _.replace(field, /attributes.+/, 'attributes'), [])
  // 比對此賣場底下全部的規格項目大於一個(自己也在這個賣場全部的規格項目裡)代表有重複
  const sameNameAttributes = _.filter(attributes, { name: value })
  if (_.size(sameNameAttributes) > 1) {
    return '不可輸入重複屬性'
  }

  return null
}

const validateCustomAttrItem = (msg, fieldItemName) => {
  // 自訂規格項目驗證
  return skipRequiredThenValidate(
    // 規格項目名稱必填
    fieldRequried({ msg }),
    // 規格項目名稱不可重複
    checkFieldNameNotDuplicated(fieldItemName)
  )
}

const validateAttrItem = (isRequired, msg) => {
  return isRequired
    ? skipRequiredThenValidate(fieldRequried({ msg }))
    : () => { }
}

const AttributeField = memo(props => {
  const {
    fieldItemName,
    field,
    index,
    disabled,
    batchApplyMode
  } = props
  const {
    hasOTHER,
    options,
    required,
    searchable,
    name,
    constraints: {
      type: constraintsType,
      maximum
    } = {}
  } = field.get(index) || {}

  const fieldName = `${fieldItemName}.selectedValues`
  let currentField = null
  switch (constraintsType) {
    case ATTRIBUTE_CONSTRAINTS_TYPE.TEXT: {
      const placeholder = `請輸入${name}`
      currentField = (
        <>
          <div className={styles.fixedWidth}>
            <Field
              name={fieldName}
              component={InputCountField}
              validate={validateAttrItem(required, placeholder)}
              className={styles.inputField}
              placeholder={placeholder}
              countLimit={maximum}
              normalizeCustomInput={getIsBSMIName(name)
                ? bsmiNormalizer
                : filterHtmlTagAndSpace}
              disabled={disabled}
              absoluteError
            />
          </div>
          <SpecAdditionalTips labelName={name} />
        </>
      )
      break
    }
    case ATTRIBUTE_CONSTRAINTS_TYPE.RADIOBOX: {
      const placeholder = `請選擇${name}`
      currentField = (
        <Field
          name={fieldName}
          component={MenuSelectField}
          validate={validateAttrItem(required, placeholder)}
          className={styles.menuSelectField}
          placeholder={placeholder}
          // 非必填可以清除選取的內容
          options={required ? options : [...options, { label: '(清除內容)', value: null }]}
          disabled={disabled}
          enableCustomInput={hasOTHER}
          multipleCustomInput={false}
          normalizeCustomInput={filterHtmlTag}
          menuInputPlaceholder='搜尋關鍵字'
          customInputPlaceholder='請輸入屬性'
          customInputButtonText='自訂其他屬性'
          customInputEditingInfo={<Tip type='info'>提醒：使用選單中的預設項目有助於搜尋</Tip>}
          closeOnSelect
          closeOnCustomInputSubmit
          editableCustomInput
          absoluteError
        />
      )
      break
    }
    case ATTRIBUTE_CONSTRAINTS_TYPE.CHECKBOX: {
      const placeholder = `請選擇${name}`
      currentField = (
        <Field
          name={fieldName}
          component={CheckboxMenuSelectField}
          validate={validateAttrItem(required, placeholder)}
          placeholder={placeholder}
          // 非必填可以清除選取的內容
          options={options}
          disabled={disabled}
          enableCustomInput={hasOTHER}
          multipleCustomInput={false}
          normalizeCustomInput={filterHtmlTag}
          menuInputPlaceholder='搜尋關鍵字'
          customInputPlaceholder='請輸入屬性'
          customInputButtonText='自訂其他屬性'
          customInputEditingInfo={<Tip type='info'>提醒：使用選單中的預設項目有助於搜尋</Tip>}
          closeOnSelect
          closeOnCustomInputSubmit
          editableCustomInput
          absoluteError
        />
      )
      break
    }
    case ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM: {
      const placeholder = '輸入規格內容'
      currentField = (
        <Field
          name={fieldName}
          component={TextareaField}
          // 自訂規格項目有名稱時對應的規格值必填
          validate={validateAttrItem(!_.isEmpty(name), placeholder)}
          className={styles.customSpecContentTextareaField}
          placeholder={placeholder}
          disabled={disabled}
          absoluteError
        />
      )
      break
    }
    default:
      break
  }

  if (_.isNull(currentField)) {
    return currentField
  }

  if (constraintsType === ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM) {
    const placeholder = '輸入規格標題'
    return (
      <FormRow
        labelAlign='top'
        className={styles.customFieldRow}
        label={(
          <div
            className={cx(
              { [styles.custom]: batchApplyMode }
            )}
          >
            {(!disabled && batchApplyMode) && (
              <Field
                className={styles.checkbox}
                id={fieldName}
                name={`${fieldName}_selected`}
                component={CheckboxInputField}
              />
            )}
            <Field
              name={`${fieldItemName}.name`}
              component={InputField}
              className={styles.customSpecTitleInputField}
              validate={validateCustomAttrItem(placeholder, fieldItemName)}
              placeholder={placeholder}
              disabled={disabled}
              absoluteError
            />
          </div>
        )}
      >
        {currentField}
        {!disabled && (
          <Button
            link
            data-role='delete-button'
            size='medium'
            className={styles.deleteCustomSpecBtn}
            // 移除此列自訂規格項目
            onClick={() => field.remove(index)}
            iconType='delete'
          />
        )}
      </FormRow>
    )
  }

  let title = name
  const isBSMIName = getIsBSMIName(name)
  if (isBSMIName) {
    title = (
      <BSMIIconPopoverLabel>
        {name}
      </BSMIIconPopoverLabel>
    )
  }
  return (
    <FormRow
      label={(
        <div>
          {(!disabled && batchApplyMode) && (
            <Field
              id={fieldName}
              name={`${fieldName}_selected`}
              component={CheckboxInputField}
            />
          )}
          {title}
        </div>
      )}
      labelAlign='center'
      subTitle={!required && '(非必填)'}
    >
      {currentField}
      {searchable && <span className={styles.info}>(可供搜尋)</span>}
    </FormRow>
  )
})

const AttributeFields = memo(props => {
  const { fields, disabled, batchApplyMode } = props
  return (
    <>
      {fields.map((fieldItemName, index, field) => {
        return (
          <AttributeField
            // fieldItemName = listings[x].attributes[x]
            key={fieldItemName}
            fieldItemName={fieldItemName}
            index={index}
            field={field}
            disabled={disabled}
            batchApplyMode={batchApplyMode}
          />
        )
      })}
      {!disabled && (
        <FormRow
          labelAlign='top'
          className={cx(
            styles.customFieldRow,
            styles.newRowBtnArea
          )}
          label={(
            <Button
              data-role='custom-spec-button'
              link
              size='medium'
              iconType='add-attachments'
              className={styles.customSpecBtn}
              // 新增一列自訂規格項目
              onClick={() => fields.push(DEFAULT_CUSTOM_ATTRIBUTE_FIELD)}
            >
              自訂規格
            </Button>
          )}
        />
      )}
    </>
  )
})

const SpecList = memo(props => {
  const { fieldPrefix, visible, disabled, batchApplyMode } = props
  return (
    <div className={styles.specList}>
      {
        visible && (
          <FieldArray
            name={getFieldName(fieldPrefix, 'attributes')}
            component={AttributeFields}
            disabled={disabled}
            batchApplyMode={batchApplyMode}
          />
        )
      }
    </div>
  )
})

export default SpecList
